import { useEffect, useState, useCallback } from 'react';
import { useChatStore } from '@/state/chatStore';
import { apiFetch } from '@/lib/auth';

export interface RosterMember { id: string; displayName: string; status: string; active?: boolean }
export interface RosterGroup { id: string; name: string; pickupEnabled: boolean; myActive?: boolean; memberCount?: number; activeCount?: number; waiting?: number; members: RosterMember[] }
export interface RosterAll { id: string; displayName: string; sharedGroup: boolean; status: string | null }
export interface Roster { groups: RosterGroup[]; all: RosterAll[] }

/**
 * Welle 9: Kombiniertes Roster (eigene Gruppen + alle Berater).
 * Status wird live über das agent:status-Event aktualisiert – aber nur für
 * Berater, mit denen man eine Gruppe teilt (Server liefert für andere status=null).
 */
export function useRoster(pollMs = 15000) {
  const [roster, setRoster] = useState<Roster>({ groups: [], all: [] });
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await apiFetch('/api/roster');
      if (!r.ok) throw new Error(String(r.status));
      const data = await r.json();
      setRoster({ groups: data.groups ?? [], all: data.all ?? [] });
    } catch (e) {
      console.warn('[roster] load failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const iv = setInterval(load, pollMs);

    function onStatus(e: any) {
      const { userId, status } = e.detail || {};
      if (!userId) return;
      setRoster((prev) => ({
        groups: prev.groups.map((g) => ({
          ...g,
          members: g.members.map((m) => (m.id === userId ? { ...m, status } : m)),
        })),
        // in "Alle" nur Status aktualisieren, wo er sichtbar ist (sharedGroup)
        all: prev.all.map((a) => (a.id === userId && a.sharedGroup ? { ...a, status } : a)),
      }));
    }
    function onLogin() { setTick((t) => t + 1); }
    window.addEventListener('bamoa:agent-status', onStatus);
    window.addEventListener('bamoa:logged-in', onLogin);
    return () => {
      clearInterval(iv);
      window.removeEventListener('bamoa:agent-status', onStatus);
      window.removeEventListener('bamoa:logged-in', onLogin);
    };
  }, [load, pollMs, tick]);

  // pre191: Anzeigenamen aus dem Roster in den Chat-Store übernehmen, damit der Chat-Peer
  // mit echtem Namen statt Fallback „Berater" erscheint (Roster gewinnt, self-healing).
  useEffect(() => {
    const map: Record<string, string> = {};
    for (const g of roster.groups) for (const m of g.members) if (m.displayName) map[m.id] = m.displayName;
    for (const a of roster.all) if (a.displayName) map[a.id] = a.displayName;
    if (Object.keys(map).length) useChatStore.setState((st) => ({ names: { ...st.names, ...map } }));
  }, [roster]);

  return { roster, loading, reload: load };
}
